/** Adding (from the picker, optionally below a widget) and removing widgets on the open page. */

import { useEffect, useRef, useState } from 'react';

import type { Panel } from './BuilderBar.tsx';
import { settle } from './grid.ts';
import { placeWidget, type WidgetSeed } from './new-widget.ts';
import type { Catalogue, Page, Widget } from './reports-api.ts';
import type { WidgetMenuActions } from './WidgetMenu.tsx';

export function useWidgetActions(input: {
  catalogue: Catalogue;
  page: Page;
  maxRows: number | null;
  autoAlign: boolean;
  setWidgets: (widgets: Widget[]) => void;
  setSelected: (index: number | null) => void;
  setPanel: (panel: Panel) => void;
  setNotice: (notice: string) => void;
  /** Every page of the document, and a way to replace them (moving a widget touches two pages). */
  pages: Page[];
  pageIndex: number;
  setPages: (pages: Page[]) => void;
}) {
  // Where the picker inserts: below this widget, or the first free spot when null.
  const [addBelow, setAddBelow] = useState<number | null>(null);
  // Copy holds a widget until it is pasted (⌘V / Ctrl+V) on this or another page.
  const [copied, setCopied] = useState<Widget | null>(null);
  const seedOf = (widget: Widget): WidgetSeed => ({
    type: widget.type,
    data: widget.data,
    display: widget.display,
  });
  const { page, setWidgets, setSelected, setPanel } = input;
  return {
    open(below: number | null) {
      setAddBelow(below);
      setPanel('library');
    },
    pick(seed: WidgetSeed) {
      const below = addBelow === null ? undefined : page.widgets[addBelow];
      const placed = placeWidget(seed, input.catalogue, page.widgets, {
        maxRows: input.maxRows,
        below,
        autoAlign: input.autoAlign,
      });
      if (!placed) {
        input.setNotice('This page is full. Make room, or add a page after this one.');
        setPanel(null);
        return;
      }
      setWidgets(placed.widgets);
      setSelected(placed.index);
      setPanel('settings');
    },
    copy(index: number) {
      const widget = page.widgets[index];
      if (!widget) return;
      setCopied(widget);
      input.setNotice('Widget copied. Press ⌘V (Ctrl+V) on any page to paste it.');
    },
    paste() {
      if (!copied) return;
      const placed = placeWidget(seedOf(copied), input.catalogue, page.widgets, {
        maxRows: input.maxRows,
        autoAlign: input.autoAlign,
      });
      if (!placed)
        return input.setNotice('This page is full. Make room, or paste on another page.');
      setWidgets(placed.widgets);
      setSelected(placed.index);
    },
    duplicate(index: number) {
      const widget = page.widgets[index];
      if (!widget) return;
      const placed = placeWidget(seedOf(widget), input.catalogue, page.widgets, {
        maxRows: input.maxRows,
        below: widget,
        autoAlign: input.autoAlign,
      });
      if (!placed)
        return input.setNotice('This page is full. Make room, or add a page after this one.');
      setWidgets(placed.widgets);
      setSelected(placed.index);
    },
    moveTo(index: number, target: number) {
      const widget = page.widgets[index];
      const targetPage = input.pages[target];
      if (!widget || !targetPage) return;
      const placed = placeWidget(seedOf(widget), input.catalogue, targetPage.widgets, {
        maxRows: input.maxRows,
        autoAlign: input.autoAlign,
      });
      if (!placed) return input.setNotice(`“${targetPage.title}” is full. Make room there first.`);
      input.setPages(
        input.pages.map((item, at) => {
          if (at === input.pageIndex)
            return {
              ...item,
              widgets: settle(
                item.widgets.filter((_, i) => i !== index),
                null,
                input.autoAlign,
              ),
            };
          if (at === target) return { ...item, widgets: placed.widgets };
          return item;
        }),
      );
      setSelected(null);
      input.setNotice(`Moved to “${targetPage.title}”.`);
    },
    hasCopy: copied !== null,
    /** Everything the widget's "⋯" menu needs, for the widget at this index. */
    menu(index: number, openFullScreen: () => void): WidgetMenuActions {
      return {
        onEdit: () => {
          setSelected(index);
          setPanel('settings');
        },
        onFullScreen: openFullScreen,
        onCopy: () => this.copy(index),
        onDuplicate: () => this.duplicate(index),
        onRemove: () => this.remove(index),
        pages: input.pages
          .map((item, at) => ({
            index: at,
            title: item.title,
            grid: (item.settings.layout ?? 'grid') === 'grid',
          }))
          .filter((item) => item.index !== input.pageIndex && item.grid),
        onMoveTo: (target: number) => this.moveTo(index, target),
      };
    },
    remove(index: number) {
      setWidgets(
        settle(
          page.widgets.filter((_, at) => at !== index),
          null,
          input.autoAlign,
        ),
      );
      setSelected(null);
      setPanel(null);
    },
  };
}

/** ⌘V / Ctrl+V pastes a copied widget, except while typing in a field. */
export function usePasteKey(paste: () => void): void {
  const latest = useRef(paste);
  latest.current = paste;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing = (event.target as HTMLElement)?.closest('input, textarea, select');
      if (typing || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'v') return;
      event.preventDefault();
      latest.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
